import { useState, type ReactNode } from 'react'
import { isAsym, isMirrored, pointEdit, sidePointEdit, type PointKey } from '@/domain/hs1'
import { Button, Modal } from '@/components/ui'
import { useSimulation } from '@/sim/SimulationProvider'
import { num } from '@/sim/parts'

/**
 * Die Rückfrage bei einem gespiegelten Punkt.
 *
 * WARUM ÜBERHAUPT GEFRAGT WIRD. Ein Zweibeiner ist symmetrisch, und wer die
 * Knieachse verschiebt, meint fast immer beide Knie. Würde die Oberfläche
 * stillschweigend beide ändern, wäre eine gewollte Unsymmetrie nicht
 * darstellbar; änderte sie stillschweigend nur eine Seite, entstünde eine
 * ungewollte Unsymmetrie, die niemand findet — bis die Maschine hinkt.
 *
 * Gefragt wird deshalb GENAU EINMAL je Punkt: beim ersten Eingriff. Danach
 * steht die Zeile entweder als symmetrisch (eine Zahl für beide) oder als
 * unsymmetrisch (zwei Spalten) da, und weitere Änderungen gehen ohne Rückfrage
 * dorthin, wo sie hingehören.
 */

export interface SideAskProps {
  open: boolean
  /** Was geändert werden soll — für den Text. */
  label: string
  axis: 'x' | 'y' | 'z'
  from: number
  to: number
  onBoth: () => void
  onLeftOnly: () => void
  onCancel: () => void
}

export function SideAsk({
  open,
  label,
  axis,
  from,
  to,
  onBoth,
  onLeftOnly,
  onCancel,
}: SideAskProps) {
  return (
    <Modal
      open={open}
      title="Beide Seiten oder nur links?"
      description={`${label} · ${axis} von ${num(from, 0)} auf ${num(to, 0)} mm`}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Abbrechen
          </Button>
          <Button variant="secondary" onClick={onLeftOnly}>
            Nur links
          </Button>
          <Button variant="primary" onClick={onBoth}>
            Beide Seiten
          </Button>
        </>
      }
    >
      <p className="page-lead">
        Diesen Punkt gibt es links wie rechts. <b>Beide Seiten</b> hält die Maschine symmetrisch:
        eine Zahl für beide Knickpunkte, wie bisher.
      </p>
      <p className="page-lead">
        <b>Nur links</b> legt eine gewollte Unsymmetrie an. Die Zeile wird ab dann mit zwei Spalten
        geführt und als unsymmetrisch gekennzeichnet; rechts bleibt bei {num(from, 0)} mm. Die
        Bahnplanung des gerechneten Gangs bleibt auf der rechten Seite — was die linke daraus macht,
        steht als Anschlag und als Fehler am Boden.
      </p>
    </Modal>
  )
}

/* -------------------------------------------------------------------------
   Der Haken, den die Tabellen benutzen
   ------------------------------------------------------------------------- */

interface Pending {
  key: PointKey
  axis: 'x' | 'y' | 'z'
  from: number
  to: number
  label: string
}

export interface PointEditor {
  /**
   * Eine Koordinate ändern.
   *
   * Bei einem gespiegelten Punkt, der noch symmetrisch ist, wird gefragt.
   * Sonst geht die Änderung direkt durch: ein mittiger Punkt hat keine zweite
   * Seite, und eine bereits unsymmetrische Zeile führt ihre Spalten getrennt.
   */
  change: (key: PointKey, axis: 'x' | 'y' | 'z', value: number | null) => void
  /** Die linke Seite allein — für die zweite Spalte einer unsymmetrischen Zeile. */
  changeLeft: (key: PointKey, axis: 'x' | 'y' | 'z', value: number | null) => void
  /** Nimmt die Unsymmetrie eines Punktes zurück: beide Seiten wieder gleich. */
  makeSymmetric: (key: PointKey) => void
  /** Der Dialog. Gehört einmal in den Seitenbaum. */
  dialog: ReactNode
}

export function usePointEditor(): PointEditor {
  const { model, setEdit } = useSimulation()
  const [pending, setPending] = useState<Pending | null>(null)

  const change: PointEditor['change'] = (key, axis, value) => {
    /* Zurücknehmen fragt nicht: „auf den Grundstand" ist immer beidseitig. */
    if (value === null || !isMirrored(key) || isAsym(model, key)) {
      setEdit(pointEdit(key, axis), value)
      return
    }
    setPending({ key, axis, from: model.points[key][axis], to: value, label: model.points[key].label })
  }

  const changeLeft: PointEditor['changeLeft'] = (key, axis, value) =>
    setEdit(sidePointEdit(key, axis), value)

  const makeSymmetric: PointEditor['makeSymmetric'] = (key) => {
    for (const axis of ['x', 'y', 'z'] as const) setEdit(sidePointEdit(key, axis), null)
  }

  const dialog = pending ? (
    <SideAsk
      open
      label={pending.label}
      axis={pending.axis}
      from={pending.from}
      to={pending.to}
      onCancel={() => setPending(null)}
      onBoth={() => {
        setEdit(pointEdit(pending.key, pending.axis), pending.to)
        setPending(null)
      }}
      onLeftOnly={() => {
        /* Rechts bleibt, wo es war — der gemeinsame Wert wird nicht angefasst. */
        setEdit(sidePointEdit(pending.key, pending.axis), pending.to)
        setPending(null)
      }}
    />
  ) : null

  return { change, changeLeft, makeSymmetric, dialog }
}
